import { api } from './client.js'
import { endpoints } from './endpoints.js'
import { unwrapList } from '../utils/format.js'

// The real ICD-10 endpoint returns { results: [{ id, code, title, label, category }] }.
// The UI code was written against { code, description }, so we normalize at
// the boundary.
function icd10Normalize(row) {
  return {
    id: row.id,
    code: row.code,
    description: row.title || row.label || '',
    title: row.title,
    label: row.label,
    category: row.category || '',
  }
}

export async function searchIcd10(q) {
  if (!q || q.trim().length < 2) return []
  const { data } = await api.get(endpoints.icd10Search, { params: { q: q.trim() } })
  const rows = unwrapList(data)
  return rows.map(icd10Normalize)
}

// Provider catalogues: use the DRF `?search=` parameter on the list endpoint.
async function paginatedSearch(url, q, extra = {}) {
  if (!q || q.trim().length < 2) return []
  const { data } = await api.get(url, { params: { search: q.trim(), ...extra } })
  return unwrapList(data)
}

// Drug formulary rows (post 2026-04 schema split):
//   { id, code, name, medication_name, strength, dosage_form,
//     generic_synonyms, is_otc, is_controlled, atc_code,
//     therapeutic_class, indicative_price_pgk, ... }
// The DrugSerializer already provides `code` (string id) and `name`
// (medication_name) for autocomplete consumption, but we re-derive them
// defensively in case the response shape varies (e.g. legacy fallback).
export async function searchDrugs(q) {
  const rows = await paginatedSearch(endpoints.drugSearch, q)
  return rows.map((r) => ({
    ...r,
    code: r.code || r.id?.toString(),
    name: r.name || r.medication_name,
    form: r.dosage_form,
    strength: r.strength,
    // Indicative price falls back to the Drug-level baseline if no
    // pharmacy-specific stock row is being looked at.
    price: r.price ?? r.indicative_price_pgk,
  }))
}

// Lab catalogue rows from the global LabTest endpoint (post 2026-04):
//   { id, code, name, test_name, short_code, loinc_code, sample_type,
//     typical_turnaround, category, synonyms, indicative_price_pgk, ... }
// We re-derive `code` and `name` defensively in case the response shape
// varies (the DrugSerializer-style aliases come from the serializer).
export async function searchLabTests(q) {
  const rows = await paginatedSearch(endpoints.labTestSearch, q)
  return rows.map((r) => ({
    ...r,
    code: r.code || r.id?.toString(),
    name: r.name || r.test_name,
    price: r.price ?? r.indicative_price_pgk,
  }))
}

// Imaging catalogue rows from the global ImagingStudy endpoint:
//   { id, code, name, exam_name, short_code, modality, modality_display,
//     body_region, contrast, requires_appointment, typical_turnaround,
//     synonyms, indicative_price_pgk, ... }
export async function searchImagingStudies(q) {
  const rows = await paginatedSearch(endpoints.imagingStudySearch, q)
  return rows.map((r) => ({
    ...r,
    code: r.code || r.id?.toString(),
    name: r.name || r.exam_name,
    price: r.price ?? r.indicative_price_pgk,
  }))
}

// Backend doesn't have a single `/organizations/?org_type=...` endpoint —
// we shell out to the per-provider list endpoint instead.
//
// Facility-type orgs (hospital/clinic/specialist/health_centre/aid_post) all
// point at the HealthFacility directory but with a `facility_type=` filter.
export async function listOrganizations(orgType, opts = {}) {
  const FACILITY_TYPES = {
    hospital: 'hospital',
    clinic: 'clinic',
    specialist: 'specialist',
    health_centre: 'health_centre',
    aid_post: 'aid_post',
  }
  if (FACILITY_TYPES[orgType]) {
    const params = { facility_type: FACILITY_TYPES[orgType] }
    if (opts.specialty) params.specialty = opts.specialty
    if (opts.search) params.search = opts.search
    if (opts.province) params.province = opts.province
    const { data } = await api.get(endpoints.facilityList, { params })
    return unwrapList(data)
  }
  const url = {
    pharmacy: endpoints.pharmacyList,
    laboratory: endpoints.laboratoryList,
    imaging: endpoints.radiologyList,
    radiology: endpoints.radiologyList,
    ambulance: endpoints.ambulanceServiceList,
  }[orgType]
  if (!url) return []
  const { data } = await api.get(url)
  return unwrapList(data)
}

// List EVERY active facility (no type filter) — used when the referral UI
// wants the doctor to pick across hospitals + clinics + specialists in a
// single combined list.
export async function listAllFacilities(opts = {}) {
  const params = {}
  if (opts.facility_type) params.facility_type = opts.facility_type
  if (opts.specialty) params.specialty = opts.specialty
  if (opts.search) params.search = opts.search
  if (opts.province) params.province = opts.province
  const { data } = await api.get(endpoints.facilityList, { params })
  return unwrapList(data)
}

export async function listFacilitySpecialties() {
  const { data } = await api.get(endpoints.facilitySpecialties)
  return unwrapList(data)
}
